<template>
  <div class="col deck-card">
    <div class="content-wrapper" @mouseenter="flip('flip')" @mouseleave="flip('unflip')">
      <div class="content" :class="{ flip: flipCard }">
        <div class="flip-card-front" @click="cardFrontClick">
          <div class="on-top" :class="{ 'in-progress-completed': deck.status }">
            <div class="deck-title-wrapper">
              <h2 class="h4 deck-title">
                {{ deck.title }}
              </h2>
              <p
                v-if="status"
                class="status"
                :class="{ [deck.status]: deck.status }"
              >
                {{ status }}
              </p>
            </div>
            <div class="author-dot-cards">
              <p class="author">
                <span class="info-names">Author:</span>
                <span class="name">{{ deck.author }}</span>
              </p>
              <p class="dot" />
              <p class="card-count">
                <span class="info-names">Cards:</span> {{ deck.cardCount }}
              </p>
            </div>
          </div>
          <img loading="lazy" :src="`${$config.imagesUrl}/flashcards/${deck.image}`" :alt="`${deck.title}’s thumbnail with a dark-blue background and visualizations of data elements`">
        </div>
        <div class="flip-card-back" :class="{ 'has-status': deck.status }">
          <div class="deck-title-wrapper">
            <div class="h5 deck-title">
              {{ deck.title }}
            </div>
          </div>
          <div class="deck-info-stats">
            <div class="author-dot-cards">
              <p class="author">
                <span class="info-names">Author:</span>
                <span class="name">{{ deck.author }}</span>
              </p>
              <p class="dot" />
              <p class="card-count">
                <span class="info-names">Cards:</span> {{ deck.cardCount }}
              </p>
            </div>
            <p v-if="deck.status">
              <span class="info-names">Status:</span> <span
                class="status"
                :class="{ 'in-progress': deck.status === 'in-progress', completed: deck.status === 'completed' }"
              >
                {{ status }} {{ statusText }}
              </span>
            </p>
          </div>

          <div class="buttons-group">
            <nuxt-link class="btn-primary" :to="buttonsHrefs[0]">
              {{ buttonsVariantsArr[0] }}
            </nuxt-link>
            <nuxt-link class="btn-primary-light" :to="buttonsHrefs[1]">
              {{ buttonsVariantsArr[1] }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    deck: { type: Object, required: true },
  },
  data() {
    return {
      flipCard: false,
    };
  },
  computed: {
    status() {
      const statuses = {
        'in-progress': 'In Progress',
        completed: 'Completed',
      };
      return statuses[this.deck.status] || '';
    },
    statusText() {
      if (!this.$store.state.auth.user) { // not logged in
        const reviewed = this.deck.cookiesReviewed;
        if (reviewed) {
          const percents = Math.round((reviewed / this.deck.cardCount) * 100);
          return `(${percents}% Reviewed)`;
        }
      }

      if (this.deck.testScore) {
        return `(${this.deck.testScore}% Mastery)`;
      }
      if (this.deck.studyScore || this.deck.studyStarted) {
        return `(${this.deck.studyScore}% Knowledge)`;
      }
      if (this.deck.reviewScore || this.deck.reviewStarted) {
        return `(${this.deck.reviewScore}% Reviewed)`;
      }
      return '';
    },
    buttonsVariantsArr() {
      if (!this.$store.state.auth.user) { // not logged in
        const reviewed = this.deck.cookiesReviewed;
        if (reviewed) {
          return ['Resume', 'View Progress']; // in progress
        }

        return ['Start', 'Preview']; // by default
      }

      // logged in
      if (this.deck.status === 'in-progress') { // in progress
        if (this.deck.studyScore === 100 && this.deck.testScore >= 60) {
          return ['Start', 'View Progress'];
        }
        return ['Resume', 'View Progress'];
      }
      if (this.deck.status === 'completed') {
        return ['Start', 'View Progress'];
      }
      return ['Start', 'Preview']; // completed or by default
    },
    buttonsHrefs() {
      if (!this.$store.state.auth.user) { // not logged in
        const reviewed = this.deck.cookiesReviewed;
        if (reviewed) {
          if (reviewed === this.deck.cardCount) { // review completed
            return [`${this.deck.slug}/study`, `${this.deck.slug}/checkpoint/?review=1`];
          }
          return [`${this.deck.slug}/review`, `${this.deck.slug}/?review=1`]; // review in progress
        }

        return [`${this.deck.slug}/review`, `${this.deck.slug}/`];
      }

      // logged in
      if (this.deck.status === 'in-progress') {
        let hrefs = [`${this.deck.slug}/review`, `${this.deck.slug}/`];
        if (this.deck.reviewScore >= 0 && this.deck.reviewScore < 100 && this.deck.studyScore === 0 && !this.deck.studyStarted) { // review started and not completed && study not started
          hrefs = [`${this.deck.slug}/review`, `${this.deck.slug}?review=1`];
        } else if (this.deck.reviewScore === 100 && this.deck.studyScore < 100) { // review completed && study not completed
          hrefs = [`${this.deck.slug}/study`, `${this.deck.slug}/checkpoint/?review=1`];
        } else if (this.deck.studyScore === 100) {
          hrefs = [`${this.deck.slug}/study`, `${this.deck.slug}/checkpoint/`];
        } else if (this.deck.studyScore > 0 || this.deck.studyStarted) {
          hrefs = [`${this.deck.slug}/study`, `${this.deck.slug}/`];
        }
        return hrefs;
      }

      if (this.deck.status === 'completed') {
        return [`${this.deck.slug}/study`, `${this.deck.slug}/`];
      }

      return [`${this.deck.slug}/review`, `${this.deck.slug}/`];
    },
  },
  methods: {
    flip(condition) {
      if (condition === 'flip') {
        this.flipCard = true;
      } else {
        this.flipCard = false;
      }
    },
    cardFrontClick() {
      if (this.$store.state.layout !== 'mobile' && !this.$store.state.laptop) {
        this.$router.push(this.buttonsHrefs[0]);
      }
    },
  },
};
</script>

<style lang="scss">
.deck-card {
  height: 230px;
  margin-bottom: var(--spacing-30);

  .content-wrapper {
    position: relative;
    z-index: 2;
    perspective: 1000px;

    .content {
      height: 230px;
      position: relative;
      z-index: 2;
      transition: transform 0.25s;
      transform-style: preserve-3d;

      @-moz-document url-prefix() {
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }

      .deck-title-wrapper {
        .h4,
        .h5 {
          margin: 0;
        }

        .deck-title {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      &.flip {
        transform: rotateX(180deg);
        transition: transform 0.25s;
      }

      .author-dot-cards .author {
        overflow: hidden;
        max-width: 230px;
        text-overflow: ellipsis;
        width: fit-content;
        white-space: nowrap;
      }

      .status {
        &.in-progress {
          color: var(--main-yellow-shade-1);
        }

        &.completed {
          color: var(--accent);
        }
      }

      .flip-card-front,
      .flip-card-back {
        position: absolute;
        width: 100%;
        height: 100%;
        background: var(--main-light);
        border-radius: 20px;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }

      .flip-card-back {
        border: 1px solid var(--main-light-shade-4);
        padding: 28.5px var(--spacing-26);
        transform: rotateX(180deg);
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;

        p {
          margin: 0;

          .status {
            &.completed {
              color: #29757A;
            }

            &.in-progress {
              color: var(--main-yellow-shade-2);
            }
          }
        }

        .deck-title-wrapper {
          display: flex;
          height: 46px;
          flex-direction: column;
          justify-content: center;
        }

        .author-dot-cards {
          display: flex;
          align-items: center;
          gap: 8px;

          .dot {
            width: 6px;
            height: 6px;
            background: var(--main-dark-light-4);
            border-radius: 50%;
          }
        }

        .info-names {
          color: var(--main-dark-light-4);
          line-height: 26px;
        }

        .buttons-group {
          display: flex;
          gap: var(--spacing-20);
          width: 100%;

          a {
            width: 50%;
            font-size: 16px;
            padding: 15px 0 var(--spacing-16) 0;

            &:first-of-type {
              &:hover {
                background: var(--accent-shade-2);
              }

              &:active {
                background: var(--accent-shade-3);
              }
            }

            &:last-of-type {
              &:hover {
                background: var(--main-light-shade-4);
                color: var(--main-light);
                border-color: var(--main-light-shade-4);
              }

              &:active {
                background: var(--main-dark-light-4);
                border-color: var(--main-dark-light-4);
              }
            }
          }
        }

        &:not(.has-status) {
          .deck-info-stats {
            display: flex;
            align-items: center;
            height: 56px;
          }
        }
      }

      .flip-card-front {
        display: block;
        overflow: hidden;
        z-index: 1;

        &:hover {
          .title {
            color: var(--accent);
          }
        }

        img {
          position: absolute;
          z-index: 0;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          right: 0;
        }

        .on-top {
          height: 100%;
          z-index: 1;
          color: var(--main-light);
          padding: var(--spacing-24) var(--spacing-26);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 34px;

          .deck-title-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            height: 122px;
            padding-top: 32px;
            gap: 12px;

            .deck-title {
              text-align: center;
            }
          }

          .author-dot-cards {
            display: flex;
            align-items: center;
            gap: 8px;

            p {
              margin: 0;
            }

            .dot {
              width: 6px;
              height: 6px;
              background: var(--main-light);
              border-radius: 50%;
            }
          }
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .deck-card {
    height: 200px;

    .content-wrapper .content {
      height: 200px;

      .status {
        margin: 0;
      }

      .author-dot-cards .author {
        max-width: 160px;
      }

      .flip-card-front .on-top .deck-title-wrapper {
        padding-top: 10px;
      }

      .flip-card-back {
        padding: var(--spacing-20);

        .deck-title-wrapper {
          height: 40px;

          .deck-title {
            font-size: 18px;
          }
        }

        .buttons-group {
          gap: var(--spacing-30);

          a {
            font-weight: 600;
            letter-spacing: 0.32px;
            padding: var(--spacing-12) 0;
          }
        }

        .info-names {
          font-size: 14px;
        }

        .name,
        .status {
          font-weight: 500;
          font-size: 14px;
        }

        .card-count {
          font-size: 14px;

          &:not(.info-names) {
            font-weight: 500;
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .deck-card {
    height: 200px;

    .content-wrapper .content {
      height: 200px;

      .status {
        margin: 0;
        line-height: 26px;
      }

      &.flip {
        transform: rotateY(180deg);
      }

      .author-dot-cards .author {
        max-width: 206px;
      }

      .flip-card-front .on-top {
        padding: 23px 10px;
        gap: 30px;

        .deck-title-wrapper {
          padding-top: 10px;
          height: 94px;

          .deck-title {
            font-size: 21px;
          }
        }
      }

      .flip-card-back {
        padding: var(--spacing-26);
        transform: rotateY(180deg);

        .deck-title-wrapper {
          height: 40px;
          margin-bottom: var(--spacing-12);

          .deck-title {
            font-size: 18px;
          }
        }

        .author-dot-cards,
        .status {
          height: 23px;
        }

        .buttons-group {
          a {
            padding: var(--spacing-12) 0;
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .deck-card .content-wrapper .content {
    .flip-card-front .on-top .deck-title-wrapper {
      .deck-title {
        padding: 0 var(--spacing-36);
      }
    }

    .flip-card-back {
      padding: 23px 13px;

      .deck-title-wrapper {
        margin: 0;
      }
    }
  }
}
</style>
